<template>
    <div class="apprentice-simple-card">
        <div class="options">
            <div class="search">
                <b-input-group>
                    <b-form-input
                        v-model="searchTerm"
                        size="md"
                        :placeholder="$t('group.search')"></b-form-input>
                    <b-input-group-append>
                        <div class="icon">
                            <i class="fas fa-search fa-fw"></i>
                        </div>
                    </b-input-group-append>
                </b-input-group>
            </div>

            <div class="page-records d-none d-sm-block">
                <label
                    class="sr-only"
                    for="per-page"
                    >{{ $t('group.by_page') }}</label
                >
                <b-form-select
                    class="apprentice-primary-select per-page-record-select"
                    id="per-page"
                    v-model="selectedRecordView"
                    size="sm"
                    :options="recordViewOptions"
                    @change="update">
                </b-form-select>
            </div>

            <div class="status">
                <b-dropdown
                    class="statuses-overview-toggle"
                    v-if="selectedOverview !== 4"
                    :text="$t('group.status')"
                    variant="primary">
                    <template #button-content>
                        <span class="label">{{ $t('group.status') }}</span>
                        <span
                            class="status-icon submitted"
                            v-b-tooltip.hover
                            :class="isSubmitted ? 'bg-submitted' : 'bg-submitted-border'"
                            :title="$t('group.submitted')">
                        </span>
                        <span
                            class="status-icon reopened"
                            v-b-tooltip.hover
                            :class="isReopened ? 'bg-reopened' : 'bg-reopened-border'"
                            :title="$t('group.reopened')">
                        </span>
                        <span
                            class="status-icon closed"
                            v-b-tooltip.hover
                            :class="isClosed ? 'bg-closed' : 'bg-closed-border'"
                            :title="$t('group.completed')">
                        </span>
                        <span
                            class="status-icon open"
                            v-b-tooltip.hover
                            :class="isOpen ? 'bg-open' : 'bg-open-border'"
                            :title="$t('common.open')">
                        </span>
                    </template>

                    <b-dropdown-form>
                        <b-form-group v-slot="{ ariaDescribedby }">
                            <b-form-checkbox-group
                                class="apprentice-checkbox statuses-checkbox-group"
                                v-model="selectedStatus"
                                :aria-describedby="ariaDescribedby"
                                name="group-statuses">
                                <b-form-checkbox value="submitted">{{ $t('group.submitted') }}</b-form-checkbox>
                                <b-form-checkbox value="reopened">{{ $t('group.reopened') }}</b-form-checkbox>
                                <b-form-checkbox value="closed">{{ $t('group.completed') }}</b-form-checkbox>
                                <b-form-checkbox value="open">{{ $t('common.open') }}</b-form-checkbox>
                            </b-form-checkbox-group>
                        </b-form-group>
                    </b-dropdown-form>
                </b-dropdown>

                <b-button
                    class="btn-download btn-export-as-excel"
                    v-else
                    @click="callDownloadExcel()"
                    ref="download-excel-button"
                    variant="primary">
                    {{ $t('common.download_excel_file') }}
                </b-button>
            </div>

            <div class="overview-select">
                <b-form-select
                    class="apprentice-primary-select portfolio-view-select"
                    id="assignment-overview-select"
                    v-model="selectedOverview"
                    :options="options">
                </b-form-select>
            </div>
        </div>

        <design-2021-group-progress-overview-component
            v-if="selectedOverview === 1"
            ref="groupProgressOverview"
            :group-data-in="groupData"
            :group-in="groupIn"
            :selected-status-in="selectedStatus"
            :search-term-in="searchTerm"
            :student-per-projects-in="studentPerProjects"
            :records-per-page="selectedRecordView">
        </design-2021-group-progress-overview-component>

        <design-2021-group-progress-category-overview-component
            v-else-if="selectedOverview === 2"
            ref="groupProgressCategoryOverview"
            :group-data-in="groupData"
            :group-in="groupIn"
            :selected-status-in="selectedStatus"
            :search-term-in="searchTerm"
            :student-per-projects-in="studentPerProjects"
            :records-per-page="selectedRecordView"
            @updateGroupData="updateGroupData">
        </design-2021-group-progress-category-overview-component>

        <design-2021-group-progress-assignments-overview-component
            v-else-if="selectedOverview === 3"
            ref="groupProgressAssignmentsOverview"
            :group-data-in="groupData"
            :group-in="groupIn"
            :selected-status-in="selectedStatus"
            :search-term-in="searchTerm"
            :student-per-projects-in="studentPerProjects"
            :records-per-page="selectedRecordView"
            @updateGroupData="updateGroupData">
        </design-2021-group-progress-assignments-overview-component>

        <design-2021-portfolio-answers-spreadsheet
            v-else-if="selectedOverview === 4"
            ref="portfolio-answers-spreadsheet"
            :search-term-in="searchTerm"
            :selected-overview-in="selectedOverview"
            :group-in="groupIn">
        </design-2021-portfolio-answers-spreadsheet>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'design-2021-group-progress-component',
    props: ['groupDataIn', 'groupIn', 'studentPerProjects', 'projectsIn'],

    provide() {
        return { rowsPerPage: this.rowsPerPage };
    },

    data() {
        return {
            groupData: this.groupDataIn,
            searchTerm: '',
            selectedOverview: 1,
            selectedStatus: ['submitted', 'reopened', 'closed', 'open'],
            options: [
                { text: this.$t('group.overview'), value: 1 },
                { text: this.$t('group.categories_overview'), value: 2 },
                { text: this.$t('group.exercise_overview'), value: 3 },
                { text: this.$t('group.answers_overview'), value: 4 },
            ],

            selectedFilter: 3,
            filterSelectOptions: [
                { value: 1, text: this.$t('group.progress_in_categories'), disabled: false },
                { value: 2, text: this.$t('group.progress_in_exercises') },
                { value: 3, text: this.$t('group.progress_in_partial_exercises') },
                { value: 4, text: this.$t('group.answers_overview') },
            ],

            selectedCategory: null,
            selectedAssigment: null,
            filter: null,
            selectedStorageName: `selectedStatus-${this.groupIn.id}${this.$authUser.id}`,
            selectedRecordView: 10,
            rowsPerPage: {
                selected: 10,
            },
            recordViewOptions: [
                {
                    value: 10,
                    text: 10,
                },
                {
                    value: 25,
                    text: 25,
                },
                {
                    value: 50,
                    text: 50,
                },
                {
                    value: 100,
                    text: 100,
                },
                {
                    value: 200,
                    text: 200,
                },
            ],
        };
    },

    mounted() {
        this.rowsPerPage.max = this.selectedRecordView;
    },

    computed: {
        fields() {
            let fieldLabel = '';

            if (this.selectedFilter === 2) {
                fieldLabel = this.$t('group.progress_in_categories');
            } else if (this.selectedFilter === 3) {
                fieldLabel = this.$t('group.progress_in_exercises');
            } else {
                fieldLabel = this.$t('group.progress_in_partial_exercises');
            }

            let fields = [
                {
                    key: 'name',
                    label: this.$t('common.first_name'),
                    sortable: true,
                },
                {
                    key: 'surname',
                    label: this.$t('common.last_name'),
                    sortable: true,
                },
                {
                    key: 'deadline_passed',
                    label: this.$t('group.passed_deadline'),
                    sortable: true,
                },
                {
                    key: 'statuses',
                    label: fieldLabel,
                },
                {
                    key: 'last_submitted',
                    label: this.$t('group.last_submitted'),
                    sortable: true,
                },
                {
                    key: 'updated_at',
                    label: this.$t('group.last_modified'),
                    sortable: true,
                },
            ];

            if (this.groupIn.children.length > 0) {
                fields.splice(2, 0, {
                    key: 'sub_project',
                    label: 'Subproject',
                    sortable: true,
                });
            }

            return fields;
        },

        availableCategories() {
            if (this.selectedOverview === 2) {
                let categoriesList = null;
                for (const assignment of this.groupData.categories.assignments) {
                    categoriesList = assignment;
                    break;
                }

                return categoriesList.categories.map((catAssignment) => {
                    return {
                        id: catAssignment.id,
                        name: catAssignment.name,
                    };
                });
            }
        },

        categorySelectOptions() {
            if (this.selectedOverview === 2 && this.availableCategories) {
                let options = [
                    {
                        value: null,
                        text: this.$t('group.choose_category'),
                    },
                ];

                for (let i = 0; i < this.availableCategories.length; i++) {
                    options.push({
                        value: this.availableCategories[i].id,
                        text: this.availableCategories[i].name,
                    });
                }

                return options;
            }
        },

        isSubmitted() {
            return this.selectedStatus.includes('submitted');
        },

        isReopened() {
            return this.selectedStatus.includes('reopened');
        },

        isClosed() {
            return this.selectedStatus.includes('closed');
        },

        isOpen() {
            return this.selectedStatus.includes('open');
        },
    },

    methods: {
        viewRoute(item) {
            switch (this.groupIn.type_id) {
                case 1:
                    return this.routes.paperShow(item.id);
                case 2:
                    return this.routes.portfolioShow(item.id);
                case 4:
                    return this.routes.magazineShow(item.id);
                case 5:
                    return this.routes.paperShow(item.id);
                case 6:
                    return this.routes.projectInternshipShow(item.id);
                case 7:
                    return this.routes.creathlonShow(item.id);
            }
        },

        updateGroupData() {
            this.axios.get(this.routes.getGroupData(this.groupIn.id)).then(
                (response) => {
                    this.groupData = response.data;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        categoryCell(key) {
            return `cell(category-${key.toLowerCase()})`;
        },

        assignmentCell(key) {
            if (key.value !== null) {
                return `cell(assignment-${key.value})`;
            }
        },

        resetOptions(selected) {
            this.selectedOverview = selected;
            if (selected === 1) {
                this.selectedFilter = 1;

                if (this.selectedCategory != null) {
                    this.selectedCategory = null;
                }

                if (this.selectedAssigment != null) {
                    this.selectedAssigment = null;
                }

                this.filterSelectOptions[0]['disabled'] = false;
            }
        },

        toolTipLabel(number, label) {
            if (label === 'closed') return `${number} ${this.$t('group.completed')}`;
            if (label === 'submitted') return `${number} ${this.$t('group.submitted')}`;
            if (label === 'reopened') return `${number} ${this.$t('group.reopened')}`;
            if (label === 'open') return `${number} ${this.$t('common.open')}`;
        },

        getStudentSubProjectName(data) {
            for (const student of this.studentPerProjects) {
                if (student.project_id === data.id && student.id === data.user_id) {
                    return student.groups.join(', ');
                }
            }
        },

        update(val) {
            this.rowsPerPage.selected = val;
        },

        callDownloadExcel() {
            this.$refs['portfolio-answers-spreadsheet'].$refs['apprentice-data-table'].$refs[
                'download-excel-button'
            ].click();
        },
    },

    created() {
        if (
            localStorage.selectedGroup &&
            parseInt(localStorage.selectedGroup) === this.groupIn.id &&
            localStorage.portfolioView
        ) {
            this.selectedOverview = parseInt(localStorage.portfolioView);
            this.selectedGroup = parseInt(localStorage.selectedGroup);

            if (localStorage.searchTerm) {
                this.filter = localStorage.searchTerm;
            }

            if (localStorage.selectedFilter) {
                this.selectedFilter = parseInt(localStorage.selectedFilter);
            }

            if (localStorage.selectedCategory && this.selectedOverview === 2) {
                const id = parseInt(localStorage.selectedCategory);
                for (const cat of this.categorySelectOptions) {
                    if (cat.value === id) {
                        this.selectedCategory = parseInt(localStorage.selectedCategory);
                    }
                }
            }
        } else {
            this.selectedGroup = this.groupIn.id;
            this.selectedFilter = 1;
            localStorage.selectedGroup = this.groupIn.id;
            localStorage.portfolioView = this.selectedOverview;
        }

        if (localStorage.getItem(this.selectedStorageName)) {
            const selectedStatusses = JSON.parse(localStorage.getItem(this.selectedStorageName));
            const now = moment();
            if (now.diff(selectedStatusses.expiry, 'days') > 1) {
                const status = {
                    value: this.selectedStatus,
                    expiry: new Date(),
                };
                localStorage.setItem(this.selectedStorageName, JSON.stringify(status));
            } else {
                const status = JSON.parse(localStorage.getItem(this.selectedStorageName));
                this.selectedStatus = status.value;
            }
        } else {
            const status = {
                value: this.selectedStatus,
                expiry: new Date(),
            };
            localStorage.setItem(this.selectedStorageName, JSON.stringify(status));
        }
    },

    watch: {
        filter: function (values) {
            localStorage.searchTerm = values;
        },

        selectedOverview: function (value) {
            localStorage.setItem('portfolioView', value);
        },

        selectedCategory: function (value) {
            if (value === null) {
                localStorage.removeItem('selectedCategory');
            } else {
                localStorage.setItem('selectedCategory', value);
            }
        },

        selectedStatus: function (value) {
            const status = {
                value: value,
                expiry: new Date(),
            };
            localStorage.setItem(this.selectedStorageName, JSON.stringify(status));
        },
    },
};
</script>

<style scoped lang="scss">
::v-deep .apprentice-table-filters {
    > div.page-records {
        display: none !important;
    }
}

.options {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    row-gap: map-get($grid-gutter-spacing, 'fifteen');
    padding-bottom: map-get($grid-gutter-spacing, 'fifteen');
    border-bottom: 1px solid $border-color;

    > div {
        flex-grow: 1;
    }

    .page-records {
        select {
            margin-bottom: 0;
        }
    }
}

.search {
    width: 100%;

    .input-group {
        &:focus-within {
            box-shadow: 0 0 0 0.2rem rgba(var(--primary), 25%);
            border-radius: $border-radius-content;
        }

        input {
            &:focus {
                box-shadow: none;
            }
        }
    }

    .input-group-append {
        color: var(--primary);
        align-items: center;
        background-color: $secondary-background-color;
        padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'ten');
        border-top-right-radius: $border-radius-content;
        border-bottom-right-radius: $border-radius-content;
    }
}

.status {
    margin-left: auto;
}

.statuses-overview-toggle ::v-deep {
    height: 51px;
    width: 100%;

    .dropdown-toggle {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        background-color: $secondary-background-color;
        border: none;
        border-radius: $border-radius-content;

        .label {
            margin-bottom: 0;
        }

        &,
        .label,
        &:after {
            font-weight: $font-weight-normal;
            color: $text-color;
        }

        &:after {
            content: '\f0dc';
            font-family: 'Font Awesome 5 Pro';
            font-weight: $font-weight-bolder;
            font-size: $base-font-size;
            border: 0;
            margin: 0;
        }
    }

    .status-icon {
        content: '';
        display: block;
        width: 15px;
        height: 15px;
        border-radius: 50%;
        transition: all 0.5s ease;
    }
}

.overview-select {
    select {
        margin-bottom: 0;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .options {
        justify-content: flex-end;
        column-gap: $grid-gutter-width;

        > div {
            flex-grow: 0;
        }
    }

    .search {
        max-width: 203px;
    }
}
</style>
